import { Decimal } from 'decimal.js';
import { z } from 'zod';
import { Currency } from './Currency.js';
import { ValidationError } from './errors/index.js';
import { MonetaryValueWithCurrency } from './MonetaryValueWithCurrency.js';
import { ValueSchema } from './schemas/MonetarySchemas.js';
const UnscaledValueSchema = z.union([z.number(), z.string()]).refine((value) => {
    try {
        const decimal = new Decimal(value);
        return decimal.isInteger();
    }
    catch {
        return false;
    }
}, 'Unscaled value must be a valid integer');
export class MonetaryAmount extends MonetaryValueWithCurrency {
    constructor(value, currency) {
        super(value, currency);
    }
    construct(value, currency) {
        return new MonetaryAmount(value, currency);
    }
    /**
     * The currencyless zero singleton — the additive identity for monetary arithmetic.
     *
     * - **Identity**: `ZERO + x = x` and `x + ZERO = x` for any MonetaryAmount x
     * - **Currency-agnostic**: Can be added to any currency without mismatch errors
     * - **Serialization**: Serializes as `{ value: "0", currency: null }`
     */
    static ZERO = new MonetaryAmount(new Decimal(0), null);
    /**
     * @param value - The scaled value of the monetary amount in major units.
     * @param currency - The Currency object or CurrencyCode.
     * @returns A new MonetaryAmount instance.
     */
    static create(value, currency) {
        const decimalValue = value instanceof Decimal ? value : new Decimal(ValueSchema.parse(value));
        return MonetaryValueWithCurrency.createFromValue(decimalValue, currency, (v, c) => new MonetaryAmount(v, c));
    }
    /**
     * @param unscaledValue - The unscaled value in the currency's smallest unit (minor units).
     * @param currency - The Currency object or CurrencyCode.
     * @returns A new MonetaryAmount instance with properly scaled value.
     */
    static createFromUnscaled(unscaledValue, currency) {
        const validatedUnscaledValue = UnscaledValueSchema.parse(unscaledValue);
        const currencyObject = currency instanceof Currency ? currency : Currency.fromCode(currency);
        const unscaledDecimal = new Decimal(validatedUnscaledValue);
        const scaledValue = unscaledDecimal.dividedBy(currencyObject.getMinorUnitDivisor());
        return new MonetaryAmount(scaledValue, currencyObject);
    }
    /**
     * Creates a zero monetary amount.
     *
     * When called without a currency, returns `MonetaryAmount.ZERO` — the currencyless
     * additive identity. When called with a currency, returns a zero amount that
     * preserves currency information.
     *
     * @param currency - Optional currency. If omitted, returns the ZERO singleton.
     * @returns A MonetaryAmount with value 0.
     */
    static zero(currency) {
        if (currency === undefined) {
            return MonetaryAmount.ZERO;
        }
        const currencyObject = currency instanceof Currency ? currency : Currency.fromCode(currency);
        return new MonetaryAmount(new Decimal(0), currencyObject);
    }
    /**
     * Creates a MonetaryAmount from a Decimal value.
     * @param value - The Decimal value.
     * @param currency - The Currency object or CurrencyCode.
     * @returns A new MonetaryAmount instance.
     */
    static from(value, currency) {
        return MonetaryValueWithCurrency.createFromValue(value, currency, (v, c) => new MonetaryAmount(v, c));
    }
    isPositive() {
        return this.getValue().isPositive();
    }
    /**
     * @returns The unscaled value in the currency's minor units, rounded HALF_EVEN to the nearest integer.
     */
    toScaled() {
        const currency = this.getCurrency();
        if (currency === null) {
            throw new ValidationError('Cannot convert to scaled value without a currency');
        }
        const divisor = currency.getMinorUnitDivisor();
        return this.getValue().times(divisor).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
    }
}
//# sourceMappingURL=MonetaryAmount.js.map